import { zodResolver } from '@hookform/resolvers/zod';
import { type Category, type UpdateCategoryInput, updateCategoryInputSchema } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';

import { Spinner } from '~/components/ui/Spinner.js';
import { TaxonomyCard } from '~/features/taxonomies/TaxonomyCard.js';
import { useCategoriesList, useUpdateCategory } from '~/features/taxonomies/hooks.js';

const categoryResolver = zodResolver(updateCategoryInputSchema);

export const Route = createFileRoute('/_auth/categories')({
  component: CategoriesPage,
});

function CategoriesPage() {
  const { data, isLoading, error, refetch } = useCategoriesList();

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Blog</p>
        <h1 className="page-title">Categorias</h1>
        <p className="page-sub">
          Os assuntos usados para organizar os artigos. Nome, descrição, cor e ordem podem ser
          mudados quando quiser. Ocultar uma categoria não apaga nenhum artigo — só a tira da
          listagem do site. O endereço da categoria é fixo, para não quebrar links já publicados.
        </p>
      </header>

      {isLoading && (
        <div className="dt__state">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <div className="dt__state">
          <p className="dt__state-title">Não foi possível carregar.</p>
          <p className="dt__state-sub">{error.displayMessage}</p>
          <button type="button" onClick={() => void refetch()} className="btn btn--secondary">
            Tentar novamente
          </button>
        </div>
      )}

      {data && (
        <section className="taxonomy-stack">
          {data.map((cat) => (
            <CategoryCard key={cat.slug} category={cat} />
          ))}
        </section>
      )}
    </div>
  );
}

function CategoryCard({ category }: { category: Category }) {
  const update = useUpdateCategory(category.slug);

  const defaults: UpdateCategoryInput = {
    nome: category.nome,
    descricao: category.descricao ?? '',
    cor: category.cor,
    ordem: category.ordem,
    visivel: category.visivel,
  };

  return (
    <TaxonomyCard<Category, UpdateCategoryInput>
      item={category}
      resolver={categoryResolver as never}
      defaults={defaults}
      onSave={(values) => update.mutateAsync(values)}
      isPending={update.isPending}
    />
  );
}
